import { Button, Form, Modal, Select } from 'antd'
import UnbillableForm, { FormValues as UnbillableFormValues } from './UnbillableForm'
import { useAccount } from '../Account/accountQueries'
import { useAddUnbillablePeriods } from '../../domain/timeTracking/timeTrackingPeriodQueries'
import { useEffect, useState } from 'react'
import { useUnbillableCategories } from '../../domain/unbillableCategories/unbillableCategoryQueries'
import dayjs from 'dayjs'
import { useCrewMembers } from '../SystemSetup/CrewMembers/crewMemberQueries'
import { useForemen } from '../../domain/users/userQueries'
import MultiSelect from '../common/FormControls/MultiSelect'

type FormValues = UnbillableFormValues & {
  foremanId: string
  crewMemberIds: number[]
}

export type Props = {
  isOpen: boolean
  onClose: (save: boolean) => void
}
const AddUnbillableModal = ({ isOpen, onClose }: Props) => {
  const account = useAccount(true)
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const timeZone = account.data?.tenant.timeZoneId || browserTimezone
  const [form] = Form.useForm()
  const addUnbillablePeriods = useAddUnbillablePeriods()
  const unbillableCategories = useUnbillableCategories()
  const defaultUnbillableCategoryId = unbillableCategories.data?.find(x => x.isDefault)?.id
  const crewMembers = useCrewMembers()
  const foremen = useForemen()

  const foremanOptions = foremen.data?.map(x => ({ label: x.displayName, value: x.userId })) ?? []
  const crewMemberOptions = crewMembers.data?.filter(x => x.isActive).map(x => ({ label: x.name, value: x.id })) ?? []

  const defaultFormValues = (): FormValues => {
    return {
      startUtc: dayjs().toISOString(),
      crewMemberIds: [],
      foremanId: '',
      unbillableCategoryId: '',
      lunch: 0,
    }
  }

  const [formValues, setFormValues] = useState<FormValues>(defaultFormValues())

  useEffect(() => {
    if (isOpen) {
      setFormValues(defaultFormValues())
    }
  }, [isOpen])

  useEffect(() => {
    if (defaultUnbillableCategoryId && formValues.unbillableCategoryId === '') {
      setFormValues({ ...formValues, unbillableCategoryId: defaultUnbillableCategoryId })
    }
  }, [defaultUnbillableCategoryId])

  const foremanId = Form.useWatch('foremanId', form)
  const crewMemberIds = Form.useWatch<number[]>('crewMemberIds', form) ?? []
  const unbillableCategoryId = Form.useWatch('unbillableCategoryId', form)

  useEffect(() => {
    if (foremanId && crewMemberIds && unbillableCategoryId) {
      setFormValues({ ...formValues, foremanId, crewMemberIds, unbillableCategoryId })
    }
  }, [foremanId, crewMemberIds, unbillableCategoryId])

  const handleSubmit = async (submit: boolean) => {
    try {
      const values = await form.validateFields() as FormValues

      if (!formValues) {
        return
      }

      const lunch = values.lunch ? values.lunch / 60 : 0
      const request = {
        ...values,
        lunch,
        endUtc: values.endUtc!
      }

      addUnbillablePeriods.mutate({ request, submit }, { onSuccess: () => onClose(true) })
    } catch { }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose(false)
  }

  return <Modal
    title="Add Unbillable Time"
    open={isOpen}
    onCancel={handleCancel}
    footer={[
      <Button key="cancel" onClick={handleCancel}>Discard Changes</Button>,
      <Button key="save" type="primary" loading={addUnbillablePeriods.isLoading} onClick={() => handleSubmit(false)}>Save</Button>,
      <Button key="saveAndSubmit" type="primary" loading={addUnbillablePeriods.isLoading} onClick={() => handleSubmit(true)}>Save and Submit</Button>,
    ]}
  >
    <Form form={form} layout="vertical">
      <Form.Item label="Foreman" name="foremanId" rules={[{ required: true, message: 'Foreman is required' }]}>
        <Select options={foremanOptions} />
      </Form.Item>
      <Form.Item
        label="Crew Members"
        name="crewMemberIds"
        required
        rules={[{
          required: true,
          message: 'Crew Members are required'
        }]}
      >
        <MultiSelect
          options={crewMemberOptions}
          selectedValues={form.getFieldValue('crewMemberIds') ?? []}
          onChange={(crewMemberIds) => form.setFieldValue('crewMemberIds', crewMemberIds)}
          maxTagCount={3}
          placeholder="Select Crew Members"
        />
      </Form.Item>
      <UnbillableForm form={form} timeZone={timeZone} />
    </Form>
  </Modal>
}

export default AddUnbillableModal